import type { DeploymentDetail } from '@central/api';
import { Card, InfoRow, StatusChip, color, formatAgo, formatBytes } from '@central/ui';
import { Table } from '@mantine/core';
import type { ReactNode } from 'react';
import { Mono } from './Mono';

const th = { fontSize: 12, color: color.textMuted, whiteSpace: 'nowrap' } as const;

function SimpleTable({
  label,
  head,
  children,
}: {
  label: string;
  head: string[];
  children: ReactNode;
}) {
  return (
    <Table.ScrollContainer minWidth={520}>
      <Table aria-label={label} verticalSpacing="sm" horizontalSpacing="md" fz={14}>
        <Table.Thead>
          <Table.Tr>
            {head.map((h) => (
              <Table.Th key={h} style={th}>
                {h}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{children}</Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Response times and open connections; rows the deployment does not report are left out. */
export function ApiCard({ api }: { api: NonNullable<DeploymentDetail['api']> }) {
  const rows: Array<[string, string]> = [];
  if (api.p50Ms !== undefined) rows.push(['Response time · median', `${api.p50Ms} ms`]);
  if (api.p95Ms !== undefined) rows.push(['Response time · 95th percentile', `${api.p95Ms} ms`]);
  if (api.openConnections !== undefined)
    rows.push(['Open connections', String(api.openConnections)]);
  if (rows.length === 0) return null;
  return (
    <Card title="Application server" style={{ flex: '2 1 280px' }}>
      {rows.map(([label, value]) => (
        <InfoRow key={label} label={label}>
          <Mono>{value}</Mono>
        </InfoRow>
      ))}
    </Card>
  );
}

/** The deployment's servers: load, and the role, OS and cores where reported. */
export function ServersCard({ nodes }: { nodes: NonNullable<DeploymentDetail['nodes']> }) {
  if (nodes.length === 0) return null;
  const role = nodes.some((n) => n.role !== undefined);
  const os = nodes.some((n) => n.os !== undefined);
  const cores = nodes.some((n) => n.cpuCores !== undefined);
  const head = ['Server', 'Status', 'CPU', 'Memory'];
  if (role) head.push('Role');
  if (os) head.push('Operating system');
  if (cores) head.push('Cores');
  return (
    <Card title="Servers" padded={false}>
      <SimpleTable label="Servers" head={head}>
        {nodes.map((n) => (
          <Table.Tr key={n.name}>
            <Table.Td>
              <Mono>{n.name}</Mono>
            </Table.Td>
            <Table.Td>
              {n.ready ? (
                <StatusChip tone="ok">Ready</StatusChip>
              ) : (
                <StatusChip tone="down">Not ready</StatusChip>
              )}
            </Table.Td>
            <Table.Td>
              <Mono>{n.cpuPercent.toFixed(0)}%</Mono>
            </Table.Td>
            <Table.Td>
              <Mono>
                {formatBytes(n.memUsedBytes)} / {formatBytes(n.memTotalBytes)}
              </Mono>
            </Table.Td>
            {role && <Table.Td>{n.role ?? '–'}</Table.Td>}
            {os && <Table.Td>{n.os ?? '–'}</Table.Td>}
            {cores && (
              <Table.Td>
                <Mono>{n.cpuCores ?? '–'}</Mono>
              </Table.Td>
            )}
          </Table.Tr>
        ))}
      </SimpleTable>
    </Card>
  );
}

/** The customer's own communication agents on this deployment. */
export function AgentsCard({ agents }: { agents: NonNullable<DeploymentDetail['agents']> }) {
  const total = agents.items.length;
  return (
    <Card
      title="Communication agents"
      action={
        total > 0 && (
          <span style={{ fontSize: 13, color: color.textMuted }}>
            {agents.connected} of {total} connected
          </span>
        )
      }
      padded={false}
    >
      {total === 0 ? (
        <p style={{ margin: 0, padding: '14px 16px', fontSize: 14, color: color.textMuted }}>
          No communication agents.
        </p>
      ) : (
        <SimpleTable
          label="Communication agents"
          head={['Agent', 'Status', 'Version', 'Operating system', 'Last check-in']}
        >
          {agents.items.map((a) => (
            <Table.Tr key={a.id}>
              <Table.Td style={{ fontWeight: 500 }}>{a.name || a.id}</Table.Td>
              <Table.Td>
                {a.connected ? (
                  <StatusChip tone="ok">Connected</StatusChip>
                ) : (
                  <StatusChip tone="down">Not connected</StatusChip>
                )}
              </Table.Td>
              <Table.Td>{a.version ? <Mono>{a.version}</Mono> : '–'}</Table.Td>
              <Table.Td>{a.os ?? '–'}</Table.Td>
              <Table.Td>{a.lastCheckInAt ? formatAgo(a.lastCheckInAt) : '–'}</Table.Td>
            </Table.Tr>
          ))}
        </SimpleTable>
      )}
      {agents.truncated && (
        <p style={{ margin: 0, padding: '10px 16px', fontSize: 13, color: color.textMuted }}>
          This deployment reports only part of its agents, so the list may be incomplete.
        </p>
      )}
    </Card>
  );
}
